/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Injectable } from '@angular/core';
import { KeyStoreEnum } from '../../enums';
import { KeyStoreModel } from '../../models';
import { LogService } from './log.service';
import { UserService } from './user.service';

/**
 * Note: we should probably generate a unique key each time the client loads and encodes the key when stored just to
 *       obfuscate things a bit more just encase some script kiddie does try to pull the details out from the browser.
 *
 */

@Injectable()
export class KeyStoreService {
    /**
     * The store state dictates the availability of the store itself to other components.
     * This acts like a mutex for security.
     * @private
     */
    private _storeState: KeyStoreEnum = KeyStoreEnum.CLOSED;

    /**
     * Keys models
     * @private
     */
    private _keys: KeyStoreModel = <KeyStoreModel>{};

    /**
     * Check if this is the login process, so we don't trigger false logs.
     * @private
     */
    private _isLoginProcess: boolean = false;

    constructor(
        private logService: LogService,
        private userService: UserService
    ) {
        this.logService.debug('KeyStoreService:constructor()', 'initiated private key store', {});
        this.userService.logOutEmitter
            .subscribe({
                next: (): void => {
                    // Shutdown our keystore
                    this.shutdown();
                }
            });
    }

    /**
     * Get the login process state.
     */
    get isLoginProcess(): boolean {
        return this._isLoginProcess;
    }

    /**
     * Set the login process state.
     */
    set isLoginProcess(state: boolean) {
        this._isLoginProcess = state;
    }

    /**
     * Get current state of the key store.
     */
    get storeState(): any {
        return this._storeState;
    }

    /**
     * Update the state of private key store. This acts as a mutex to prevent components referencing our user private key
     * if any other component gets nervous for whatever reason.
     * @param state
     */
    set storeState(state: KeyStoreEnum) {
        this.logService.debug('KeyStoreService:storeState()', 'updated key store state', { state });
        this._storeState = state;

        if (state === KeyStoreEnum.OPEN && !this.isLoginProcess) {
            if (!this.orgPrivateKey) {
                this.orgPrivateKey = sessionStorage.getItem('orgKey');
                if (!this.orgPrivateKey) {
                    this.logService.warn(
                        'KeyStoreService: storeState',
                        'Missing orgPrivateKey data for an open store in session storage'
                    );
                }
            }

            if (!this.userPrivateKey) {
                this.userPrivateKey = sessionStorage.getItem('userKey');
                if (!this.userPrivateKey) {
                    this.logService.warn(
                        'KeyStoreService: storeState',
                        'Missing userPrivateKey data for an open store in session storage'
                    );
                }
            }
        }
    }

    /**
     * Returns the private user key.
     * This is ONLY accessible if the key store is open.
     */
    get userPrivateKey(): string {
        if (this._storeState === KeyStoreEnum.CLOSED) {
            this.logService.error('KeyStoreService', 'failed to make changes to the key store', {});

            return;
        }

        return this._keys.UserPrivateKey;
    }

    /**
     * Set the user private key for reference later.
     * Note: The keystore MUST be open in order to make changes.
     * @param key
     */
    set userPrivateKey(key: string) {
        if (this._storeState === KeyStoreEnum.CLOSED) {
            this.logService.error('KeyStoreService', 'failed to make changes to the key store', {});

            return;
        }
        this.logService.debug('KeyStoreService:userPrivateKey()', 'updated key store', { keys: this._keys });
        this._keys.UserPrivateKey = key;
        sessionStorage.setItem('userKey', key);
    }

    /**
     * Returns the private org key.
     * This is ONLY accessible if the key store is open.
     */
    get orgPrivateKey(): string {
        if (this._storeState === KeyStoreEnum.CLOSED) {
            this.logService.error('KeyStoreService', 'failed to make changes to the key store', {});

            return;
        }

        return this._keys.OrgPrivateKey;
    }

    /**
     * Set the user private key for reference later.
     * Note: The keystore MUST be open in order to make changes.
     * @param key
     */
    set orgPrivateKey(key: string) {
        if (this._storeState === KeyStoreEnum.CLOSED) {
            this.logService.error('KeyStoreService', 'failed to make changes to the key store', {});

            return;
        }
        this.logService.debug('KeyStoreService:userPrivateKey()', 'updated key store', { keys: this._keys });
        this._keys.OrgPrivateKey = key;
        sessionStorage.setItem('orgKey', key);
    }

    /**
     * Get the private key from the keystore.
     */
    public getPrivateKeyFromKeystore(getOrgKey?: any): any {
        if (this.storeState === KeyStoreEnum.CLOSED) {
            this.logService.error(
                'KeyStoreService',
                'The keystore is closed!'
            );
        }

        return getOrgKey ? this.orgPrivateKey : this.userPrivateKey;
    }

    /**
     * Shutdown resets our private key and closes the key store.
     */
    public shutdown(): void {
        this.userPrivateKey = '';
        this.orgPrivateKey = '';
        this.storeState = KeyStoreEnum.CLOSED;
    }
}
